'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@ui/components/ui/sheet';
import { LocationPickerContent } from '@website/components/LocationPicker/LocationPickerContent';

import { LocationPickerHeader } from './LocationPickerHeader';

interface LocationPickerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (location: { latitude: number; longitude: number; name: string }) => void;
}

/**
 * LocationPickerDrawer - Full-screen overlay for location picker on mobile.
 *
 * Built on the shared Sheet primitive so it portals to <body>. A hand-rolled
 * `fixed inset-0` overlay rendered inline beside the results map gets punched
 * through by the map's GPU-composited layer on iOS Safari (the white panel
 * vanished and the map showed behind the header). Portaling out of the map's
 * stacking context — plus Radix's iOS-safe scroll lock — fixes that.
 */
export const LocationPickerDrawer: React.FC<LocationPickerDrawerProps> = ({ isOpen, onClose, onLocationSelect }) => {
  const t = useTranslations();

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        // Override the Sheet's default padding/rounding to fill the screen and
        // host the picker's own header. Let the picker input claim focus on
        // open instead of the Sheet's first focusable.
        className="h-[100dvh] max-h-[100dvh] w-full gap-0 border-0 p-0 flex flex-col"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetTitle className="sr-only">{t('SearchPage.chooseLocation')}</SheetTitle>
        <SheetDescription className="sr-only">{t('SearchPage.searchLocation')}</SheetDescription>
        <div className="flex-shrink-0">
          <LocationPickerHeader onBackClick={onClose} />
        </div>
        <div className="flex-1 min-h-0 px-5 pb-5">
          <LocationPickerContent onLocationSelect={onLocationSelect} />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LocationPickerDrawer;
